<script lang="ts" setup>
import { useSwiper } from '~/composables/common/use-swiper'
import type { GameItem, GamesRequestPayload } from '@kira-dancer/nuxt-common'
import {
  SpaceBetweenSwiper,
  DepositRouteEnum,
  RoutesEnum,
  GameGroupEnum,
  SlidersPerView,
} from '~/enums'
import { usePartnerProvider } from '~/composables/common/use-partner-providers'
import { useDevice } from '#imports'

const { modules, SwiperSlide, Swiper } = useSwiper()
const { logoPartners } = usePartnerProvider()
const { navigate } = useNavigate()
const authStore = useAuthStore()
const route = useRoute()
const { isMobile } = useDevice()
const props = defineProps({
  isShowWelcomeModal: {
    type: Boolean,
    required: true,
  },
})
const { BRAND_NAME, FLAT_CURRENCY_UNIT } = useRuntimeConfig().public
const partners = [
  logoPartners[0],
  logoPartners[3],
  logoPartners[4],
  logoPartners[14],
  logoPartners[7],
  logoPartners[8],
  logoPartners[9],
]
const LIMIT_GAME = 5
const isVisible = computed(() => {
  return props.isShowWelcomeModal
})

const lobbyGames = reactive<GameItem[]>([])

const { requestFetchGames } = useLobbyGames()

const params: GamesRequestPayload = {
  groups: GameGroupEnum.Welcome,
  limit: LIMIT_GAME,
}

const fetchGames = async () => {
  try {
    const response = await requestFetchGames(params)
    if (response) {
      lobbyGames.length = 0
      lobbyGames.push(...response)
    }
  } catch {
    lobbyGames.length = 0
  }
}

watch(
  isVisible,
  (newValue) => {
    if (newValue) {
      fetchGames()
    }
  },
  { immediate: true },
)

watch(
  () => route.path,
  () => {
    authStore.setEnableWelcomeModal(false)
  },
)

const redirectToPage = (url: string) => {
  navigate(url)
  authStore.setEnableWelcomeModal(false)
}
</script>
<template>
  <BaseModal
    v-model="isVisible"
    class="welcome"
    width="934"
    :hide-close-button="true"
  >
    <div class="header-icon">
      <img src="/images/home/welcome-top-bg.webp" alt="banner welcome" />
    </div>
    <div class="header-box">
      <span
        >{{ BRAND_NAME }} tặng bạn
        <span class="highlight"> 20,000,000 {{ FLAT_CURRENCY_UNIT }} </span>
        lần nạp đầu tiên
      </span>
    </div>
    <BaseSection title="Trò chơi đề xuất" class="modal-section">
      <div v-if="lobbyGames?.length">
        <div v-if="!isMobile" class="content-games">
          <CommonGameItem
            v-for="item in lobbyGames.slice(0, 5)"
            :key="item.gameId"
            :item="item"
          />
        </div>
        <div v-else>
          <Swiper
            :modules="modules"
            :pagination="{
              el: '.game-banners-pagination',
              clickable: true,
            }"
            :slides-per-view="SlidersPerView.MediumSmall"
            :space-between="8"
          >
            <SwiperSlide
              v-for="item in lobbyGames.slice(0, 5)"
              :key="item.gameId"
            >
              <CommonGameItem :item="item" />
            </SwiperSlide>
          </Swiper>
          <div v-if="isMobile" class="pagination">
            <div class="game-banners-pagination"></div>
          </div>
        </div>
      </div>
    </BaseSection>
    <BaseSection class="modal-section">
      <Swiper
        v-if="isMobile"
        :modules="modules"
        :slides-per-view="4.5"
        :space-between="SpaceBetweenSwiper.Small"
        class="mt-[8px] ctn-providers"
      >
        <SwiperSlide v-for="item in partners" :key="item.title">
          <img :src="item.logoMb" :alt="item.title" class="logo" />
        </SwiperSlide>
      </Swiper>
      <div v-else class="content-providers">
        <div v-for="item in partners" :key="item.title">
          <img
            :src="item.logo"
            :alt="item.title"
            class="providers__list--logo"
          />
        </div>
      </div>
    </BaseSection>
    <DesktopHomePromotion
      :has-view-more="false"
      :space-between="16"
      title="Khuyến mãi thành viên"
      :is-modal="true"
      class="modal-section"
    />
    <div class="content-btn">
      <div class="ctn-btn">
        <BaseButton
          class="button-custom"
          type="button"
          label="Vào trang chủ"
          size="large"
          button-id="redirect-home"
          button-name="redirect-home"
          @click="redirectToPage(RoutesEnum.Home)"
        />
      </div>
      <div class="ctn-btn">
        <BaseButton
          class="button-custom"
          variant="primary"
          type="button"
          size="large"
          label="Nhận thưởng ngay"
          button-id="redirect-to-deposit"
          button-name="redirect-to-deposit"
          @click="redirectToPage(DepositRouteEnum.Deposit)"
        />
      </div>
    </div>
  </BaseModal>
</template>
<style lang="scss" scoped>
@import '~/assets/styles/components/modal/welcome.scss';
</style>
